/**
 * Tooltip 工具函数模块。
 * 放置 tooltip 相关的工具函数，包括数值格式化、样式更新等。
 * 如需新增 tooltip 展示相关的基础工具，请添加在此文件。
 */
import Decimal from 'decimal.js';
import { formatDisplayValue } from '@customComp/Chart/_config/_utils';
import { TOOLTIP_WRAP_CLASS_NAME } from './constants';

interface IUpdateTooltipSizeParams {
  /** tooltip 的最大高度（像素） */
  height: number;
  /** tooltip 的最大宽度（像素） */
  width: number;
  /** tooltip 外层容器的 CSS 类名，默认为 TOOLTIP_WRAP_CLASS_NAME */
  wrapClassName?: string;
}

interface ITooltipValueConfig {
  isPercent: boolean;
  isDecimalPercent?: boolean;
  marker: string;
  accuracy: number;
  unit: string;
}

export const tooltipValueFormat = (
  value: string | number,
  config: ITooltipValueConfig,
): string => {
  const { isPercent, marker, accuracy, unit, isDecimalPercent } = config;
  const text = formatDisplayValue({
    value: (isDecimalPercent && value) ? Decimal.mul(Number(value) || 0, 100).toString() : value,
    isPercent,
    accuracy,
    unit,
  });

  return `${marker}<span style="color: #EBECF1;font-size: 14px;font-weight: 700;">${text || '-'}</span>`;
};

/**
 * 更新 tooltip 的 CSS 变量，根据传入的尺寸设置 tooltip 的最大宽高
 * 内部使用 requestAnimationFrame 确保 tooltip DOM 已创建并渲染后再更新
 * 添加了安全检查，防止组件卸载后访问已移除的 DOM 元素
 * @param params - 更新参数对象
 * @param params.height - tooltip 的最大高度（像素）
 * @param params.width - tooltip 的最大宽度（像素）
 * @param params.wrapClassName - tooltip 外层容器的 CSS 类名，默认为 TOOLTIP_WRAP_CLASS_NAME
 */
export const updateTooltipSize = ({
  height,
  width,
  wrapClassName = TOOLTIP_WRAP_CLASS_NAME,
}: IUpdateTooltipSizeParams): void => {
  // 使用 requestAnimationFrame 确保 tooltip DOM 已创建并渲染
  requestAnimationFrame(() => {
    try {
      // 更新所有已存在的 tooltip 元素的 CSS 变量
      // 因为 tooltip 是 appendToBody，不会继承图表容器的 CSS 变量
      const tooltipWraps = document.querySelectorAll(`.${wrapClassName}`);
      tooltipWraps.forEach((wrap) => {
        // 安全检查：确保元素仍在 DOM 中且有效
        if (wrap && document.body.contains(wrap)) {
          (wrap as HTMLElement).style.setProperty('--chart-max-height', `${height}px`);
          (wrap as HTMLElement).style.setProperty('--chart-max-width', `${width}px`);
        }
      });
    } catch (error) {
      // 静默处理错误，避免影响用户体验
      console.warn('[updateTooltipSize] Failed to update tooltip size:', error);
    }
  });
};


